import { api } from '@/config/api';

export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type TicketStatus = 'open' | 'pending' | 'on_hold' | 'resolved' | 'closed';
export type TicketSource = 'manual' | 'email' | 'whatsapp' | 'web' | 'phone';
export type SlaState = 'met' | 'on_track' | 'at_risk' | 'breached';

export interface TicketComment {
  commentUid: string;
  authorUserUid: string;
  body: string;
  internal: boolean;
  createdAt: string;
}

export interface TicketEvent {
  type: 'created' | 'status_changed' | 'priority_changed' | 'assigned' | 'comment_added';
  fromValue?: string;
  toValue?: string;
  byUserUid?: string;
  at: string;
}

export interface SlaClock {
  dueAt: string | null;
  state: SlaState;
}

export interface Ticket {
  ticketUid: string;
  companyUid: string;
  number: number;
  subject: string;
  description?: string;
  contactUid?: string;
  requesterName?: string;
  requesterEmail?: string;
  requesterPhone?: string;
  priority: TicketPriority;
  status: TicketStatus;
  category?: string;
  tags: string[];
  assigneeUserUid?: string;
  departmentUid?: string;
  source: TicketSource;
  firstResponseDueAt?: string;
  resolutionDueAt?: string;
  firstRespondedAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  pausedAt?: string;
  pausedMinutes?: number;
  comments: TicketComment[];
  events: TicketEvent[];
  createdByUserUid: string;
  createdAt: string;
  updatedAt: string;
  sla: { firstResponse: SlaClock; resolution: SlaClock };
}

export interface TicketListResponse { items: Ticket[]; total: number; page: number; limit: number }

export interface TicketStats {
  open: number;
  breached: number;
  dueToday: number;
  unassigned: number;
  avgFirstResponseMinutes: number | null;
  avgResolutionMinutes: number | null;
}

export interface SlaWindow { firstResponseMinutes: number; resolutionMinutes: number }
export interface SlaPolicy { urgent: SlaWindow; high: SlaWindow; normal: SlaWindow; low: SlaWindow }
export interface TicketSettings { companyUid: string; slaPolicy: SlaPolicy; businessHoursOnly: boolean }

export interface ListTicketsParams {
  status?: TicketStatus;
  priority?: TicketPriority;
  /** A userUid, or the literal 'me' / 'unassigned'. */
  assignee?: string;
  slaState?: 'at_risk' | 'breached';
  q?: string;
  page?: number;
  limit?: number;
}

export interface CreateTicketInput {
  subject: string;
  description?: string;
  contactUid?: string;
  requesterName?: string;
  requesterEmail?: string;
  requesterPhone?: string;
  priority?: TicketPriority;
  category?: string;
  tags?: string[];
  assigneeUserUid?: string;
  departmentUid?: string;
  source?: TicketSource;
}

export interface UpdateTicketInput {
  subject?: string;
  description?: string;
  contactUid?: string | null;
  priority?: TicketPriority;
  category?: string;
  tags?: string[];
  assigneeUserUid?: string | null;
  departmentUid?: string | null;
}

const BASE = '/slighter_crm/tickets';

export const ticketsService = {
  list: async (params: ListTicketsParams = {}): Promise<TicketListResponse> => (await api.get(BASE, { params })).data,
  stats: async (): Promise<TicketStats> => (await api.get(`${BASE}/stats`)).data,
  get: async (ticketUid: string): Promise<Ticket> => (await api.get(`${BASE}/${ticketUid}`)).data,
  create: async (input: CreateTicketInput): Promise<Ticket> => (await api.post(BASE, input)).data,
  update: async (ticketUid: string, patch: UpdateTicketInput): Promise<Ticket> => (await api.patch(`${BASE}/${ticketUid}`, patch)).data,
  setStatus: async (ticketUid: string, status: TicketStatus): Promise<Ticket> =>
    (await api.patch(`${BASE}/${ticketUid}/status`, { status })).data,
  addComment: async (ticketUid: string, body: string, internal: boolean): Promise<Ticket> =>
    (await api.post(`${BASE}/${ticketUid}/comments`, { body, internal })).data,
  getSlaPolicy: async (): Promise<TicketSettings> => (await api.get(`${BASE}/sla-policy`)).data,
  updateSlaPolicy: async (payload: { slaPolicy: SlaPolicy; businessHoursOnly?: boolean }): Promise<TicketSettings> =>
    (await api.put(`${BASE}/sla-policy`, payload)).data,
};
